import type { Locale } from './locales';

/** TBM 페이지에서 사용하는 정적 UI 키 */
export type TbmStaticKey =
  | 'common.loading'
  | 'common.error'
  | 'common.cancel'
  | 'common.confirm'
  | 'common.submit'
  | 'common.next'
  | 'common.previous'
  | 'common.close'
  | 'common.languageSwitch'
  | 'sign.title'
  | 'sign.description'
  | 'sign.namePlaceholder'
  | 'sign.signaturePlaceholder'
  | 'sign.signButton'
  | 'sign.success'
  | 'sign.expired'
  | 'sign.alreadySigned'
  | 'sign.invalidToken'
  | 'sign.legalNotice'
  | 'educator.title'
  | 'educator.description'
  | 'educator.startEducation'
  | 'educator.completeEducation'
  | 'educator.attendeesTitle'
  | 'educator.directSign'
  | 'educator.signatureRequired'
  | 'educator.educatorSignature'
  | 'educator.legalNotice'
  | 'tbm.duration'
  | 'tbm.educator'
  | 'tbm.location'
  | 'tbm.contents'
  | 'tbm.workType'
  | 'tbm.specialNotes'
  | 'tbm.safetyMeasures'
  | 'tbm.workDescription'
  | 'tbm.findings'
  | 'tbm.attendance'
  | 'tbm.signedAt'
  | 'manager.attendees'
  | 'manager.directSignReceive'
  | 'manager.signed'
  | 'manager.notSigned'
  | 'manager.educationDate'
  | 'manager.tbmContents'
  | 'manager.hazardFindings'
  | 'manager.measures'
  | 'manager.caption'
  | 'manager.criticalHazard'
  | 'manager.languageSwitchHint'
  | 'badge.severity.critical'
  | 'badge.severity.high'
  | 'badge.severity.medium'
  | 'badge.severity.low'
  | 'badge.signed'
  | 'badge.waiting'
  | 'badge.status.creating'
  | 'badge.status.created'
  | 'badge.status.reviewing'
  | 'badge.status.inProgress'
  | 'badge.status.pendingApproval'
  | 'badge.status.completed'
  | 'badge.status.rejected'
  | 'badge.status.cancelled'
  | 'badge.sms.sent'
  | 'badge.sms.failed'
  | 'badge.approvalPending'
  | 'badge.tag.urgent'
  | 'badge.tag.special'
  | 'badge.tag.extra';

/**
 * 모든 11개 locale 사전 번역.
 *
 * 번역 출처: Google Translate (한국어 원문 → 10개 언어 1회 번역).
 * 변경/추가 시 ko 키 작성 후 Google Translate로 10개 일괄 번역하여 채움.
 */
export const TBM_STATIC_STRINGS: Record<Locale, Record<TbmStaticKey, string>> = {
  ko: {
    'common.loading': '불러오는 중...',
    'common.error': '오류가 발생했습니다',
    'common.cancel': '취소',
    'common.confirm': '확인',
    'common.submit': '제출',
    'common.next': '다음',
    'common.previous': '이전',
    'common.close': '닫기',
    'common.languageSwitch': '언어 선택',
    'sign.title': 'TBM 교육 서명',
    'sign.description': '아래에 이름과 서명을 입력하여 TBM 교육 참석을 확인해 주세요.',
    'sign.namePlaceholder': '이름을 입력하세요',
    'sign.signaturePlaceholder': '서명란',
    'sign.signButton': '서명 완료',
    'sign.success': '서명이 완료되었습니다.',
    'sign.expired': '교육 기간이 만료되었습니다.',
    'sign.alreadySigned': '이미 서명이 완료된 항목입니다.',
    'sign.invalidToken': '유효하지 않은 링크입니다.',
    'sign.legalNotice': '본 서명은 법적 효력이 있는 전자 서명입니다.',
    'educator.title': 'TBM 교육 진행',
    'educator.description': '교육 참석자를 확인하고 교육을 진행해 주세요.',
    'educator.startEducation': '교육 시작',
    'educator.completeEducation': '교육 완료',
    'educator.attendeesTitle': '참석자 목록',
    'educator.directSign': '직접 서명',
    'educator.signatureRequired': '교육담당자 서명이 필요합니다.',
    'educator.educatorSignature': '교육담당자 서명',
    'educator.legalNotice': '교육담당자 서명은 교육 완료를 법적으로 증빙합니다.',
    'tbm.duration': '교육 시간',
    'tbm.educator': '교육담당자',
    'tbm.location': '교육 장소',
    'tbm.contents': '교육 내용',
    'tbm.workType': '작업 유형',
    'tbm.specialNotes': '특이사항',
    'tbm.safetyMeasures': '안전조치',
    'tbm.workDescription': '작업 설명',
    'tbm.findings': '위험 요인',
    'tbm.attendance': '참석 현황',
    'tbm.signedAt': '서명 일시',
    'manager.attendees': '교육 참가자',
    'manager.directSignReceive': '직접 서명 받기',
    'manager.signed': '서명 완료',
    'manager.notSigned': '미서명',
    'manager.educationDate': '교육 일자',
    'manager.tbmContents': 'TBM 내용',
    'manager.hazardFindings': '잠재위험요인',
    'manager.measures': '대책',
    'manager.caption': '사진 설명',
    'manager.criticalHazard': '중점위험요인',
    'manager.languageSwitchHint': '언어 전환 (외국인 근로자용)',
    'badge.severity.critical': '심각',
    'badge.severity.high': '높음',
    'badge.severity.medium': '보통',
    'badge.severity.low': '낮음',
    'badge.signed': '서명 완료',
    'badge.waiting': '대기',
    'badge.status.creating': '생성 중',
    'badge.status.created': '생성됨',
    'badge.status.reviewing': '확인 중',
    'badge.status.inProgress': '진행 중',
    'badge.status.pendingApproval': '결재 대기',
    'badge.status.completed': '완료',
    'badge.status.rejected': '반려됨',
    'badge.status.cancelled': '취소됨',
    'badge.sms.sent': 'SMS 발송됨',
    'badge.sms.failed': 'SMS 실패',
    'badge.approvalPending': '대기 중',
    'badge.tag.urgent': '긴급',
    'badge.tag.special': '특별',
    'badge.tag.extra': '추가',
  },
  en: {
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.submit': 'Submit',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    'common.languageSwitch': 'Language',
    'sign.title': 'TBM Education Signature',
    'sign.description': 'Please enter your name and signature below to confirm your attendance at the TBM education.',
    'sign.namePlaceholder': 'Enter your name',
    'sign.signaturePlaceholder': 'Signature area',
    'sign.signButton': 'Complete Signature',
    'sign.success': 'Signature completed successfully.',
    'sign.expired': 'The education period has expired.',
    'sign.alreadySigned': 'This item has already been signed.',
    'sign.invalidToken': 'Invalid link.',
    'sign.legalNotice': 'This signature is a legally valid electronic signature.',
    'educator.title': 'TBM Education Session',
    'educator.description': 'Please verify attendees and proceed with the education.',
    'educator.startEducation': 'Start Education',
    'educator.completeEducation': 'Complete Education',
    'educator.attendeesTitle': 'Attendee List',
    'educator.directSign': 'Direct Sign',
    'educator.signatureRequired': 'Educator signature is required.',
    'educator.educatorSignature': 'Educator Signature',
    'educator.legalNotice': 'The educator signature legally certifies the completion of education.',
    'tbm.duration': 'Duration',
    'tbm.educator': 'Educator',
    'tbm.location': 'Location',
    'tbm.contents': 'Contents',
    'tbm.workType': 'Work Type',
    'tbm.specialNotes': 'Special Notes',
    'tbm.safetyMeasures': 'Safety Measures',
    'tbm.workDescription': 'Work Description',
    'tbm.findings': 'Hazard Findings',
    'tbm.attendance': 'Attendance',
    'tbm.signedAt': 'Signed At',
    'manager.attendees': 'Attendees',
    'manager.directSignReceive': 'Collect Direct Signature',
    'manager.signed': 'Signed',
    'manager.notSigned': 'Not Signed',
    'manager.educationDate': 'Education Date',
    'manager.tbmContents': 'TBM Contents',
    'manager.hazardFindings': 'Hazard Findings',
    'manager.measures': 'Measures',
    'manager.caption': 'Photo Caption',
    'manager.criticalHazard': 'Critical Hazard',
    'manager.languageSwitchHint': 'Language (for foreign workers)',
    'badge.severity.critical': 'Critical',
    'badge.severity.high': 'High',
    'badge.severity.medium': 'Medium',
    'badge.severity.low': 'Low',
    'badge.signed': 'Signed',
    'badge.waiting': 'Waiting',
    'badge.status.creating': 'Creating',
    'badge.status.created': 'Created',
    'badge.status.reviewing': 'Reviewing',
    'badge.status.inProgress': 'In Progress',
    'badge.status.pendingApproval': 'Pending Approval',
    'badge.status.completed': 'Completed',
    'badge.status.rejected': 'Rejected',
    'badge.status.cancelled': 'Cancelled',
    'badge.sms.sent': 'SMS Sent',
    'badge.sms.failed': 'SMS Failed',
    'badge.approvalPending': 'Pending',
    'badge.tag.urgent': 'Urgent',
    'badge.tag.special': 'Special',
    'badge.tag.extra': 'Extra',
  },
  vi: {
    'common.loading': 'Đang tải...',
    'common.error': 'Đã xảy ra lỗi',
    'common.cancel': 'Hủy',
    'common.confirm': 'Xác nhận',
    'common.submit': 'Gửi',
    'common.next': 'Tiếp theo',
    'common.previous': 'Trước',
    'common.close': 'Đóng',
    'common.languageSwitch': 'Chọn ngôn ngữ',
    'sign.title': 'Chữ ký buổi đào tạo TBM',
    'sign.description': 'Vui lòng nhập tên và chữ ký bên dưới để xác nhận tham gia buổi đào tạo TBM.',
    'sign.namePlaceholder': 'Nhập tên của bạn',
    'sign.signaturePlaceholder': 'Khu vực chữ ký',
    'sign.signButton': 'Hoàn thành chữ ký',
    'sign.success': 'Chữ ký đã được hoàn thành.',
    'sign.expired': 'Thời gian đào tạo đã hết hạn.',
    'sign.alreadySigned': 'Mục này đã được ký.',
    'sign.invalidToken': 'Liên kết không hợp lệ.',
    'sign.legalNotice': 'Chữ ký này là chữ ký điện tử có hiệu lực pháp lý.',
    'educator.title': 'Buổi đào tạo TBM',
    'educator.description': 'Vui lòng xác nhận người tham dự và tiến hành đào tạo.',
    'educator.startEducation': 'Bắt đầu đào tạo',
    'educator.completeEducation': 'Hoàn thành đào tạo',
    'educator.attendeesTitle': 'Danh sách người tham dự',
    'educator.directSign': 'Ký trực tiếp',
    'educator.signatureRequired': 'Yêu cầu chữ ký của giáo viên.',
    'educator.educatorSignature': 'Chữ ký giáo viên',
    'educator.legalNotice': 'Chữ ký của giáo viên chứng nhận pháp lý việc hoàn thành đào tạo.',
    'tbm.duration': 'Thời lượng',
    'tbm.educator': 'Giáo viên',
    'tbm.location': 'Địa điểm',
    'tbm.contents': 'Nội dung',
    'tbm.workType': 'Loại công việc',
    'tbm.specialNotes': 'Ghi chú đặc biệt',
    'tbm.safetyMeasures': 'Biện pháp an toàn',
    'tbm.workDescription': 'Mô tả công việc',
    'tbm.findings': 'Yếu tố nguy hiểm',
    'tbm.attendance': 'Tình trạng tham dự',
    'tbm.signedAt': 'Thời gian ký',
    'manager.attendees': 'Người tham dự',
    'manager.directSignReceive': 'Thu thập chữ ký trực tiếp',
    'manager.signed': 'Đã ký',
    'manager.notSigned': 'Chưa ký',
    'manager.educationDate': 'Ngày đào tạo',
    'manager.tbmContents': 'Nội dung TBM',
    'manager.hazardFindings': 'Yếu tố nguy hiểm tiềm ẩn',
    'manager.measures': 'Biện pháp',
    'manager.caption': 'Chú thích ảnh',
    'manager.criticalHazard': 'Yếu tố nguy hiểm trọng tâm',
    'manager.languageSwitchHint': 'Ngôn ngữ (dành cho lao động nước ngoài)',
    'badge.severity.critical': 'Nghiêm trọng',
    'badge.severity.high': 'Cao',
    'badge.severity.medium': 'Trung bình',
    'badge.severity.low': 'Thấp',
    'badge.signed': 'Đã ký',
    'badge.waiting': 'Đang chờ',
    'badge.status.creating': 'Đang tạo',
    'badge.status.created': 'Đã tạo',
    'badge.status.reviewing': 'Đang xem xét',
    'badge.status.inProgress': 'Đang tiến hành',
    'badge.status.pendingApproval': 'Chờ phê duyệt',
    'badge.status.completed': 'Hoàn thành',
    'badge.status.rejected': 'Bị từ chối',
    'badge.status.cancelled': 'Đã hủy',
    'badge.sms.sent': 'Đã gửi SMS',
    'badge.sms.failed': 'Gửi SMS thất bại',
    'badge.approvalPending': 'Đang chờ',
    'badge.tag.urgent': 'Khẩn cấp',
    'badge.tag.special': 'Đặc biệt',
    'badge.tag.extra': 'Bổ sung',
  },
  th: {
    'common.loading': 'กำลังโหลด...',
    'common.error': 'เกิดข้อผิดพลาด',
    'common.cancel': 'ยกเลิก',
    'common.confirm': 'ยืนยัน',
    'common.submit': 'ส่ง',
    'common.next': 'ถัดไป',
    'common.previous': 'ก่อนหน้า',
    'common.close': 'ปิด',
    'common.languageSwitch': 'เลือกภาษา',
    'sign.title': 'ลายเซ็นการอบรม TBM',
    'sign.description': 'กรุณากรอกชื่อและลายเซ็นด้านล่างเพื่อยืนยันการเข้าร่วมการอบรม TBM',
    'sign.namePlaceholder': 'กรอกชื่อของคุณ',
    'sign.signaturePlaceholder': 'พื้นที่ลายเซ็น',
    'sign.signButton': 'เซ็นลายเซ็นเสร็จสิ้น',
    'sign.success': 'ลายเซ็นเสร็จสมบูรณ์แล้ว',
    'sign.expired': 'ระยะเวลาการอบรมหมดอายุแล้ว',
    'sign.alreadySigned': 'รายการนี้ได้รับการลงนามแล้ว',
    'sign.invalidToken': 'ลิงก์ไม่ถูกต้อง',
    'sign.legalNotice': 'ลายเซ็นนี้เป็นลายเซ็นอิเล็กทรอนิกส์ที่มีผลทางกฎหมาย',
    'educator.title': 'การดำเนินการอบรม TBM',
    'educator.description': 'กรุณายืนยันผู้เข้าร่วมและดำเนินการอบรม',
    'educator.startEducation': 'เริ่มการอบรม',
    'educator.completeEducation': 'เสร็จสิ้นการอบรม',
    'educator.attendeesTitle': 'รายชื่อผู้เข้าร่วม',
    'educator.directSign': 'เซ็นโดยตรง',
    'educator.signatureRequired': 'ต้องการลายเซ็นของผู้สอน',
    'educator.educatorSignature': 'ลายเซ็นผู้สอน',
    'educator.legalNotice': 'ลายเซ็นของผู้สอนรับรองทางกฎหมายถึงการเสร็จสิ้นการอบรม',
    'tbm.duration': 'ระยะเวลา',
    'tbm.educator': 'ผู้สอน',
    'tbm.location': 'สถานที่',
    'tbm.contents': 'เนื้อหา',
    'tbm.workType': 'ประเภทงาน',
    'tbm.specialNotes': 'หมายเหตุพิเศษ',
    'tbm.safetyMeasures': 'มาตรการความปลอดภัย',
    'tbm.workDescription': 'คำอธิบายงาน',
    'tbm.findings': 'ปัจจัยอันตราย',
    'tbm.attendance': 'สถานะการเข้าร่วม',
    'tbm.signedAt': 'เวลาที่ลงนาม',
    'manager.attendees': 'ผู้เข้าร่วม',
    'manager.directSignReceive': 'รับลายเซ็นโดยตรง',
    'manager.signed': 'ลงนามแล้ว',
    'manager.notSigned': 'ยังไม่ลงนาม',
    'manager.educationDate': 'วันที่อบรม',
    'manager.tbmContents': 'เนื้อหา TBM',
    'manager.hazardFindings': 'ปัจจัยอันตรายที่อาจเกิดขึ้น',
    'manager.measures': 'มาตรการ',
    'manager.caption': 'คำบรรยายภาพ',
    'manager.criticalHazard': 'ปัจจัยอันตรายหลัก',
    'manager.languageSwitchHint': 'ภาษา (สำหรับแรงงานต่างชาติ)',
    'badge.severity.critical': 'วิกฤต',
    'badge.severity.high': 'สูง',
    'badge.severity.medium': 'ปานกลาง',
    'badge.severity.low': 'ต่ำ',
    'badge.signed': 'ลงนามแล้ว',
    'badge.waiting': 'รอดำเนินการ',
    'badge.status.creating': 'กำลังสร้าง',
    'badge.status.created': 'สร้างแล้ว',
    'badge.status.reviewing': 'กำลังตรวจสอบ',
    'badge.status.inProgress': 'กำลังดำเนินการ',
    'badge.status.pendingApproval': 'รออนุมัติ',
    'badge.status.completed': 'เสร็จสิ้น',
    'badge.status.rejected': 'ถูกปฏิเสธ',
    'badge.status.cancelled': 'ยกเลิกแล้ว',
    'badge.sms.sent': 'ส่ง SMS แล้ว',
    'badge.sms.failed': 'ส่ง SMS ล้มเหลว',
    'badge.approvalPending': 'รอดำเนินการ',
    'badge.tag.urgent': 'เร่งด่วน',
    'badge.tag.special': 'พิเศษ',
    'badge.tag.extra': 'เพิ่มเติม',
  },
  uz: {
    'common.loading': 'Yuklanmoqda...',
    'common.error': 'Xatolik yuz berdi',
    'common.cancel': 'Bekor qilish',
    'common.confirm': 'Tasdiqlash',
    'common.submit': 'Yuborish',
    'common.next': 'Keyingi',
    'common.previous': 'Oldingi',
    'common.close': 'Yopish',
    'common.languageSwitch': 'Tilni tanlang',
    'sign.title': 'TBM ta\'lim imzosi',
    'sign.description': 'TBM ta\'limda ishtirokingizni tasdiqlash uchun quyida ism va imzongizni kiriting.',
    'sign.namePlaceholder': 'Ismingizni kiriting',
    'sign.signaturePlaceholder': 'Imzo maydoni',
    'sign.signButton': 'Imzolashni yakunlash',
    'sign.success': 'Imzo muvaffaqiyatli yakunlandi.',
    'sign.expired': 'Ta\'lim muddati tugagan.',
    'sign.alreadySigned': 'Bu element allaqachon imzolangan.',
    'sign.invalidToken': 'Noto\'g\'ri havola.',
    'sign.legalNotice': 'Bu imzo yuridik jihatdan amal qiladigan elektron imzodir.',
    'educator.title': 'TBM ta\'lim sessiyasi',
    'educator.description': 'Qatnashchilarni tasdiqlang va ta\'limni o\'tkazing.',
    'educator.startEducation': 'Ta\'limni boshlash',
    'educator.completeEducation': 'Ta\'limni yakunlash',
    'educator.attendeesTitle': 'Qatnashchilar ro\'yxati',
    'educator.directSign': 'Bevosita imzolash',
    'educator.signatureRequired': 'O\'qituvchi imzosi talab qilinadi.',
    'educator.educatorSignature': 'O\'qituvchi imzosi',
    'educator.legalNotice': 'O\'qituvchi imzosi ta\'lim yakunlanishini huquqiy jihatdan tasdiqlaydi.',
    'tbm.duration': 'Davomiyligi',
    'tbm.educator': 'O\'qituvchi',
    'tbm.location': 'Joylashuv',
    'tbm.contents': 'Tarkib',
    'tbm.workType': 'Ish turi',
    'tbm.specialNotes': 'Maxsus izohlar',
    'tbm.safetyMeasures': 'Xavfsizlik choralari',
    'tbm.workDescription': 'Ish tavsifi',
    'tbm.findings': 'Xavf omillari',
    'tbm.attendance': 'Qatnashuv holati',
    'tbm.signedAt': 'Imzolangan vaqt',
    'manager.attendees': 'Qatnashchilar',
    'manager.directSignReceive': 'To\'g\'ridan-to\'g\'ri imzo olish',
    'manager.signed': 'Imzolangan',
    'manager.notSigned': 'Imzolanmagan',
    'manager.educationDate': 'Ta\'lim sanasi',
    'manager.tbmContents': 'TBM tarkibi',
    'manager.hazardFindings': 'Potensial xavf omillari',
    'manager.measures': 'Chora-tadbirlar',
    'manager.caption': 'Rasm tavsifi',
    'manager.criticalHazard': 'Asosiy xavf omili',
    'manager.languageSwitchHint': 'Til (chet el ishchilari uchun)',
    'badge.severity.critical': 'Jiddiy',
    'badge.severity.high': 'Yuqori',
    'badge.severity.medium': 'O\'rta',
    'badge.severity.low': 'Past',
    'badge.signed': 'Imzolangan',
    'badge.waiting': 'Kutilmoqda',
    'badge.status.creating': 'Yaratilmoqda',
    'badge.status.created': 'Yaratilgan',
    'badge.status.reviewing': 'Ko\'rib chiqilmoqda',
    'badge.status.inProgress': 'Jarayonda',
    'badge.status.pendingApproval': 'Tasdiqlash kutilmoqda',
    'badge.status.completed': 'Yakunlangan',
    'badge.status.rejected': 'Rad etilgan',
    'badge.status.cancelled': 'Bekor qilingan',
    'badge.sms.sent': 'SMS yuborildi',
    'badge.sms.failed': 'SMS yuborilmadi',
    'badge.approvalPending': 'Kutilmoqda',
    'badge.tag.urgent': 'Shoshilinch',
    'badge.tag.special': 'Maxsus',
    'badge.tag.extra': 'Qo\'shimcha',
  },
  ne: {
    'common.loading': 'लोड हुँदैछ...',
    'common.error': 'त्रुटि भयो',
    'common.cancel': 'रद्द गर्नुहोस्',
    'common.confirm': 'पुष्टि गर्नुहोस्',
    'common.submit': 'पेश गर्नुहोस्',
    'common.next': 'अर्को',
    'common.previous': 'अघिल्लो',
    'common.close': 'बन्द गर्नुहोस्',
    'common.languageSwitch': 'भाषा चयन गर्नुहोस्',
    'sign.title': 'TBM शिक्षा हस्ताक्षर',
    'sign.description': 'TBM शिक्षामा उपस्थिति पुष्टि गर्न तलको नाम र हस्ताक्षर प्रविष्ट गर्नुहोस्।',
    'sign.namePlaceholder': 'आफ्नो नाम लेख्नुहोस्',
    'sign.signaturePlaceholder': 'हस्ताक्षर क्षेत्र',
    'sign.signButton': 'हस्ताक्षर पूरा गर्नुहोस्',
    'sign.success': 'हस्ताक्षर सफलतापूर्वक पूरा भयो।',
    'sign.expired': 'शिक्षा अवधि समाप्त भएको छ।',
    'sign.alreadySigned': 'यो आइटम पहिले नै हस्ताक्षर गरिएको छ।',
    'sign.invalidToken': 'अमान्य लिंक।',
    'sign.legalNotice': 'यो हस्ताक्षर कानूनी रूपमा मान्य इलेक्ट्रोनिक हस्ताक्षर हो।',
    'educator.title': 'TBM शिक्षा सत्र',
    'educator.description': 'सहभागीहरू प्रमाणित गर्नुहोस् र शिक्षा अगाडि बढाउनुहोस्।',
    'educator.startEducation': 'शिक्षा सुरु गर्नुहोस्',
    'educator.completeEducation': 'शिक्षा पूरा गर्नुहोस्',
    'educator.attendeesTitle': 'सहभागी सूची',
    'educator.directSign': 'प्रत्यक्ष हस्ताक्षर',
    'educator.signatureRequired': 'शिक्षकको हस्ताक्षर आवश्यक छ।',
    'educator.educatorSignature': 'शिक्षकको हस्ताक्षर',
    'educator.legalNotice': 'शिक्षकको हस्ताक्षरले शिक्षा पूरा भएको कानूनी प्रमाण दिन्छ।',
    'tbm.duration': 'अवधि',
    'tbm.educator': 'शिक्षक',
    'tbm.location': 'स्थान',
    'tbm.contents': 'सामग्री',
    'tbm.workType': 'काम को प्रकार',
    'tbm.specialNotes': 'विशेष नोटहरू',
    'tbm.safetyMeasures': 'सुरक्षा उपायहरू',
    'tbm.workDescription': 'काम को विवरण',
    'tbm.findings': 'खतरा कारकहरू',
    'tbm.attendance': 'उपस्थिति स्थिति',
    'tbm.signedAt': 'हस्ताक्षर समय',
    'manager.attendees': 'सहभागीहरू',
    'manager.directSignReceive': 'प्रत्यक्ष हस्ताक्षर संकलन',
    'manager.signed': 'हस्ताक्षर गरिएको',
    'manager.notSigned': 'हस्ताक्षर नगरिएको',
    'manager.educationDate': 'शिक्षा मिति',
    'manager.tbmContents': 'TBM सामग्री',
    'manager.hazardFindings': 'सम्भावित खतरा कारकहरू',
    'manager.measures': 'उपायहरू',
    'manager.caption': 'फोटो क्याप्सन',
    'manager.criticalHazard': 'मुख्य खतरा कारक',
    'manager.languageSwitchHint': 'भाषा (विदेशी कामदारहरूका लागि)',
    'badge.severity.critical': 'गम्भीर',
    'badge.severity.high': 'उच्च',
    'badge.severity.medium': 'मध्यम',
    'badge.severity.low': 'न्यून',
    'badge.signed': 'हस्ताक्षर गरिएको',
    'badge.waiting': 'प्रतीक्षारत',
    'badge.status.creating': 'सिर्जना हुँदैछ',
    'badge.status.created': 'सिर्जना भयो',
    'badge.status.reviewing': 'समीक्षा हुँदैछ',
    'badge.status.inProgress': 'प्रक्रियामा',
    'badge.status.pendingApproval': 'अनुमोदनको प्रतीक्षामा',
    'badge.status.completed': 'पूरा भयो',
    'badge.status.rejected': 'अस्वीकृत',
    'badge.status.cancelled': 'रद्द गरियो',
    'badge.sms.sent': 'SMS पठाइयो',
    'badge.sms.failed': 'SMS असफल',
    'badge.approvalPending': 'प्रतीक्षारत',
    'badge.tag.urgent': 'अत्यावश्यक',
    'badge.tag.special': 'विशेष',
    'badge.tag.extra': 'थप',
  },
  my: {
    'common.loading': 'တင်နေသည်...',
    'common.error': 'အမှားတစ်ခုဖြစ်ပေါ်ခဲ့သည်',
    'common.cancel': 'ပယ်ဖျက်',
    'common.confirm': 'အတည်ပြု',
    'common.submit': 'တင်သွင်း',
    'common.next': 'နောက်တစ်ခု',
    'common.previous': 'အရင်',
    'common.close': 'ပိတ်',
    'common.languageSwitch': 'ဘာသာစကားရွေးပါ',
    'sign.title': 'TBM သင်တန်း လက်မှတ်',
    'sign.description': 'TBM သင်တန်းတက်ရောက်မှုကို အတည်ပြုရန် အောက်တွင် နာမည်နှင့် လက်မှတ်ရေးထိုးပါ။',
    'sign.namePlaceholder': 'သင့်နာမည်ထည့်ပါ',
    'sign.signaturePlaceholder': 'လက်မှတ်နေရာ',
    'sign.signButton': 'လက်မှတ်ပြီးစီး',
    'sign.success': 'လက်မှတ်ရေးထိုးပြီးစီးသည်။',
    'sign.expired': 'သင်တန်းကာလ သက်တမ်းကုန်သွားပြီ။',
    'sign.alreadySigned': 'ဤပစ္စည်းကို ရေးထိုးပြီးသားဖြစ်သည်။',
    'sign.invalidToken': 'မမှန်ကန်သောလင့်ခ်ဖြစ်သည်။',
    'sign.legalNotice': 'ဤလက်မှတ်သည် တရားဝင် အီလက်ထရောနစ် လက်မှတ်ဖြစ်သည်။',
    'educator.title': 'TBM သင်တန်းဆောင်ရွက်',
    'educator.description': 'တက်ရောက်သူများကို အတည်ပြုပြီး သင်တန်းဆင်းပါ။',
    'educator.startEducation': 'သင်တန်းစတင်',
    'educator.completeEducation': 'သင်တန်းပြီးစီး',
    'educator.attendeesTitle': 'တက်ရောက်သူစာရင်း',
    'educator.directSign': 'တိုက်ရိုက်လက်မှတ်',
    'educator.signatureRequired': 'ဆရာ၏လက်မှတ်လိုအပ်သည်။',
    'educator.educatorSignature': 'ဆရာ၏လက်မှတ်',
    'educator.legalNotice': 'ဆရာ၏လက်မှတ်သည် သင်တန်းပြီးစီးကြောင်း တရားဝင်သက်သေပြုသည်။',
    'tbm.duration': 'ကြာချိန်',
    'tbm.educator': 'ဆရာ',
    'tbm.location': 'နေရာ',
    'tbm.contents': 'အကြောင်းအရာ',
    'tbm.workType': 'အလုပ်အမျိုးအစား',
    'tbm.specialNotes': 'အထူးမှတ်ချက်များ',
    'tbm.safetyMeasures': 'လုံခြုံရေးအစီအမံများ',
    'tbm.workDescription': 'အလုပ်ဖော်ပြချက်',
    'tbm.findings': 'အန္တရာယ်အချက်များ',
    'tbm.attendance': 'တက်ရောက်မှုအခြေအနေ',
    'tbm.signedAt': 'လက်မှတ်ထိုးချိန်',
    'manager.attendees': 'တက်ရောက်သူများ',
    'manager.directSignReceive': 'တိုက်ရိုက်လက်မှတ်ထိုးရယူခြင်း',
    'manager.signed': 'လက်မှတ်ထိုးပြီး',
    'manager.notSigned': 'လက်မှတ်မထိုးရသေး',
    'manager.educationDate': 'သင်တန်းနေ့',
    'manager.tbmContents': 'TBM အကြောင်းအရာ',
    'manager.hazardFindings': 'ဖြစ်နိုင်သည့်အန္တရာယ်အချက်များ',
    'manager.measures': 'ကြိုတင်ကာကွယ်မှုများ',
    'manager.caption': 'ဓာတ်ပုံ ဖော်ပြချက်',
    'manager.criticalHazard': 'အဓိကအန္တရာယ်အချက်',
    'manager.languageSwitchHint': 'ဘာသာစကား (နိုင်ငံခြား အလုပ်သမားများအတွက်)',
    'badge.severity.critical': 'အပြင်းအထန်',
    'badge.severity.high': 'မြင့်မား',
    'badge.severity.medium': 'အလတ်စား',
    'badge.severity.low': 'နိမ့်',
    'badge.signed': 'လက်မှတ်ထိုးပြီး',
    'badge.waiting': 'စောင့်ဆိုင်းနေ',
    'badge.status.creating': 'ဖန်တီးနေသည်',
    'badge.status.created': 'ဖန်တီးပြီး',
    'badge.status.reviewing': 'သုံးသပ်နေသည်',
    'badge.status.inProgress': 'လုပ်ဆောင်နေသည်',
    'badge.status.pendingApproval': 'အတည်ပြုချက်စောင့်နေ',
    'badge.status.completed': 'ပြီးစီး',
    'badge.status.rejected': 'ငြင်းပယ်ခဲ့',
    'badge.status.cancelled': 'ပယ်ဖျက်ပြီး',
    'badge.sms.sent': 'SMS ပို့ပြီး',
    'badge.sms.failed': 'SMS မအောင်မြင်',
    'badge.approvalPending': 'စောင့်ဆိုင်းနေ',
    'badge.tag.urgent': 'အရေးပေါ်',
    'badge.tag.special': 'အထူး',
    'badge.tag.extra': 'ထပ်ဆောင်း',
  },
  km: {
    'common.loading': 'កំពុងផ្ទុក...',
    'common.error': 'មានបញ្ហាកើតឡើង',
    'common.cancel': 'បោះបង់',
    'common.confirm': 'បញ្ជាក់',
    'common.submit': 'ដាក់ស្នើ',
    'common.next': 'បន្ទាប់',
    'common.previous': 'មុន',
    'common.close': 'បិទ',
    'common.languageSwitch': 'ជ្រើសរើសភាសា',
    'sign.title': 'ហត្ថលេខាការបណ្តុះបណ្តាល TBM',
    'sign.description': 'សូមបញ្ចូលឈ្មោះ និងហត្ថលេខារបស់អ្នកខាងក្រោម ដើម្បីបញ្ជាក់ការចូលរួមបណ្តុះបណ្តាល TBM។',
    'sign.namePlaceholder': 'បញ្ចូលឈ្មោះរបស់អ្នក',
    'sign.signaturePlaceholder': 'តំបន់ហត្ថលេខា',
    'sign.signButton': 'បញ្ចប់ហត្ថលេខា',
    'sign.success': 'ហត្ថលេខាត្រូវបានបញ្ចប់ដោយជោគជ័យ។',
    'sign.expired': 'រយៈពេលបណ្តុះបណ្តាលបានផុតកំណត់ហើយ។',
    'sign.alreadySigned': 'ធាតុនេះត្រូវបានហត្ថលេខារួចហើយ។',
    'sign.invalidToken': 'តំណភ្ជាប់មិនត្រឹមត្រូវ។',
    'sign.legalNotice': 'ហត្ថលេខានេះជាហត្ថលេខាអេឡិចត្រូនិកដែលមានសុពលភាពស្របច្បាប់។',
    'educator.title': 'វគ្គបណ្តុះបណ្តាល TBM',
    'educator.description': 'សូមផ្ទៀងផ្ទាត់អ្នកចូលរួម ហើយបន្តការបណ្តុះបណ្តាល។',
    'educator.startEducation': 'ចាប់ផ្តើមបណ្តុះបណ្តាល',
    'educator.completeEducation': 'បញ្ចប់ការបណ្តុះបណ្តាល',
    'educator.attendeesTitle': 'បញ្ជីអ្នកចូលរួម',
    'educator.directSign': 'ហត្ថលេខាដោយផ្ទាល់',
    'educator.signatureRequired': 'ត្រូវការហត្ថលេខារបស់គ្រូ។',
    'educator.educatorSignature': 'ហត្ថលេខាគ្រូ',
    'educator.legalNotice': 'ហត្ថលេខារបស់គ្រូបញ្ជាក់ស្របច្បាប់ថាការបណ្តុះបណ្តាលបានបញ្ចប់។',
    'tbm.duration': 'រយៈពេល',
    'tbm.educator': 'គ្រូ',
    'tbm.location': 'ទីតាំង',
    'tbm.contents': 'មាតិកា',
    'tbm.workType': 'ប្រភេទការងារ',
    'tbm.specialNotes': 'កំណត់ចំណាំពិសេស',
    'tbm.safetyMeasures': 'វិធានការសុវត្ថិភាព',
    'tbm.workDescription': 'ការពិពណ៌នាការងារ',
    'tbm.findings': 'កត្តាគ្រោះថ្នាក់',
    'tbm.attendance': 'ស្ថានភាពចូលរួម',
    'tbm.signedAt': 'ពេលវេលាហត្ថលេខា',
    'manager.attendees': 'អ្នកចូលរួម',
    'manager.directSignReceive': 'ទទួលហត្ថលេខាដោយផ្ទាល់',
    'manager.signed': 'បានហត្ថលេខា',
    'manager.notSigned': 'មិនទាន់ហត្ថលេខា',
    'manager.educationDate': 'កាលបរិច្ឆេទបណ្តុះបណ្តាល',
    'manager.tbmContents': 'មាតិកា TBM',
    'manager.hazardFindings': 'កត្តាគ្រោះថ្នាក់ដែលអាចកើតមាន',
    'manager.measures': 'វិធានការ',
    'manager.caption': 'ចំណងជើងរូបភាព',
    'manager.criticalHazard': 'កត្តាគ្រោះថ្នាក់សំខាន់',
    'manager.languageSwitchHint': 'ភាសា (សម្រាប់កម្មករបរទេស)',
    'badge.severity.critical': 'ធ្ងន់ធ្ងរ',
    'badge.severity.high': 'ខ្ពស់',
    'badge.severity.medium': 'មធ្យម',
    'badge.severity.low': 'ទាប',
    'badge.signed': 'បានហត្ថលេខា',
    'badge.waiting': 'កំពុងរង់ចាំ',
    'badge.status.creating': 'កំពុងបង្កើត',
    'badge.status.created': 'បានបង្កើត',
    'badge.status.reviewing': 'កំពុងពិនិត្យ',
    'badge.status.inProgress': 'កំពុងដំណើរការ',
    'badge.status.pendingApproval': 'រង់ចាំការអនុម័ត',
    'badge.status.completed': 'បានបញ្ចប់',
    'badge.status.rejected': 'ត្រូវបានបដិសេធ',
    'badge.status.cancelled': 'ត្រូវបានលុបចោល',
    'badge.sms.sent': 'បានផ្ញើ SMS',
    'badge.sms.failed': 'ការផ្ញើ SMS បានបរាជ័យ',
    'badge.approvalPending': 'កំពុងរង់ចាំ',
    'badge.tag.urgent': 'បន្ទាន់',
    'badge.tag.special': 'ពិសេស',
    'badge.tag.extra': 'បន្ថែម',
  },
  id: {
    'common.loading': 'Memuat...',
    'common.error': 'Terjadi kesalahan',
    'common.cancel': 'Batal',
    'common.confirm': 'Konfirmasi',
    'common.submit': 'Kirim',
    'common.next': 'Berikutnya',
    'common.previous': 'Sebelumnya',
    'common.close': 'Tutup',
    'common.languageSwitch': 'Pilih Bahasa',
    'sign.title': 'Tanda Tangan Pelatihan TBM',
    'sign.description': 'Masukkan nama dan tanda tangan Anda di bawah ini untuk mengkonfirmasi kehadiran pelatihan TBM.',
    'sign.namePlaceholder': 'Masukkan nama Anda',
    'sign.signaturePlaceholder': 'Area tanda tangan',
    'sign.signButton': 'Selesaikan Tanda Tangan',
    'sign.success': 'Tanda tangan berhasil diselesaikan.',
    'sign.expired': 'Periode pelatihan telah berakhir.',
    'sign.alreadySigned': 'Item ini sudah ditandatangani.',
    'sign.invalidToken': 'Tautan tidak valid.',
    'sign.legalNotice': 'Tanda tangan ini adalah tanda tangan elektronik yang sah secara hukum.',
    'educator.title': 'Sesi Pelatihan TBM',
    'educator.description': 'Verifikasi peserta dan lanjutkan pelatihan.',
    'educator.startEducation': 'Mulai Pelatihan',
    'educator.completeEducation': 'Selesaikan Pelatihan',
    'educator.attendeesTitle': 'Daftar Peserta',
    'educator.directSign': 'Tanda Tangan Langsung',
    'educator.signatureRequired': 'Tanda tangan instruktur diperlukan.',
    'educator.educatorSignature': 'Tanda Tangan Instruktur',
    'educator.legalNotice': 'Tanda tangan instruktur mensertifikasi penyelesaian pelatihan secara hukum.',
    'tbm.duration': 'Durasi',
    'tbm.educator': 'Instruktur',
    'tbm.location': 'Lokasi',
    'tbm.contents': 'Konten',
    'tbm.workType': 'Jenis Pekerjaan',
    'tbm.specialNotes': 'Catatan Khusus',
    'tbm.safetyMeasures': 'Tindakan Keselamatan',
    'tbm.workDescription': 'Deskripsi Pekerjaan',
    'tbm.findings': 'Faktor Bahaya',
    'tbm.attendance': 'Status Kehadiran',
    'tbm.signedAt': 'Waktu Penandatanganan',
    'manager.attendees': 'Peserta',
    'manager.directSignReceive': 'Kumpulkan Tanda Tangan Langsung',
    'manager.signed': 'Sudah Ditandatangani',
    'manager.notSigned': 'Belum Ditandatangani',
    'manager.educationDate': 'Tanggal Pelatihan',
    'manager.tbmContents': 'Konten TBM',
    'manager.hazardFindings': 'Faktor Bahaya Potensial',
    'manager.measures': 'Tindakan',
    'manager.caption': 'Keterangan Foto',
    'manager.criticalHazard': 'Faktor Bahaya Kritis',
    'manager.languageSwitchHint': 'Bahasa (untuk pekerja asing)',
    'badge.severity.critical': 'Kritis',
    'badge.severity.high': 'Tinggi',
    'badge.severity.medium': 'Sedang',
    'badge.severity.low': 'Rendah',
    'badge.signed': 'Sudah Ditandatangani',
    'badge.waiting': 'Menunggu',
    'badge.status.creating': 'Sedang Dibuat',
    'badge.status.created': 'Dibuat',
    'badge.status.reviewing': 'Sedang Ditinjau',
    'badge.status.inProgress': 'Sedang Berlangsung',
    'badge.status.pendingApproval': 'Menunggu Persetujuan',
    'badge.status.completed': 'Selesai',
    'badge.status.rejected': 'Ditolak',
    'badge.status.cancelled': 'Dibatalkan',
    'badge.sms.sent': 'SMS Terkirim',
    'badge.sms.failed': 'SMS Gagal',
    'badge.approvalPending': 'Menunggu',
    'badge.tag.urgent': 'Mendesak',
    'badge.tag.special': 'Khusus',
    'badge.tag.extra': 'Tambahan',
  },
  tl: {
    'common.loading': 'Naglo-load...',
    'common.error': 'Nagkaroon ng error',
    'common.cancel': 'Kanselahin',
    'common.confirm': 'Kumpirmahin',
    'common.submit': 'Isumite',
    'common.next': 'Susunod',
    'common.previous': 'Nakaraan',
    'common.close': 'Isara',
    'common.languageSwitch': 'Pumili ng Wika',
    'sign.title': 'Lagda sa Pagsasanay ng TBM',
    'sign.description': 'Mangyaring ilagay ang iyong pangalan at lagda sa ibaba upang kumpirmahin ang iyong pagdalo sa pagsasanay ng TBM.',
    'sign.namePlaceholder': 'Ilagay ang iyong pangalan',
    'sign.signaturePlaceholder': 'Lugar ng lagda',
    'sign.signButton': 'Kumpletuhin ang Lagda',
    'sign.success': 'Matagumpay na nakumpleto ang lagda.',
    'sign.expired': 'Ang panahon ng pagsasanay ay nag-expire na.',
    'sign.alreadySigned': 'Ang item na ito ay nalagdaan na.',
    'sign.invalidToken': 'Hindi wastong link.',
    'sign.legalNotice': 'Ang lagdang ito ay isang legal na may bisa na electronic signature.',
    'educator.title': 'Sesyon ng Pagsasanay ng TBM',
    'educator.description': 'Mangyaring i-verify ang mga kalahok at magpatuloy sa pagsasanay.',
    'educator.startEducation': 'Simulan ang Pagsasanay',
    'educator.completeEducation': 'Kumpletuhin ang Pagsasanay',
    'educator.attendeesTitle': 'Listahan ng mga Kalahok',
    'educator.directSign': 'Direktang Lagda',
    'educator.signatureRequired': 'Kinakailangan ang lagda ng guro.',
    'educator.educatorSignature': 'Lagda ng Guro',
    'educator.legalNotice': 'Ang lagda ng guro ay legal na nagsertipiko ng pagkumpleto ng pagsasanay.',
    'tbm.duration': 'Tagal',
    'tbm.educator': 'Guro',
    'tbm.location': 'Lokasyon',
    'tbm.contents': 'Mga Nilalaman',
    'tbm.workType': 'Uri ng Trabaho',
    'tbm.specialNotes': 'Mga Espesyal na Tala',
    'tbm.safetyMeasures': 'Mga Hakbang sa Kaligtasan',
    'tbm.workDescription': 'Paglalarawan ng Trabaho',
    'tbm.findings': 'Mga Salik ng Panganib',
    'tbm.attendance': 'Katayuan ng Pagdalo',
    'tbm.signedAt': 'Oras ng Paglagda',
    'manager.attendees': 'Mga Kalahok',
    'manager.directSignReceive': 'Mangolekta ng Direktang Lagda',
    'manager.signed': 'Nalagdaan na',
    'manager.notSigned': 'Hindi pa Nalagdaan',
    'manager.educationDate': 'Petsa ng Pagsasanay',
    'manager.tbmContents': 'Mga Nilalaman ng TBM',
    'manager.hazardFindings': 'Mga Potensyal na Salik ng Panganib',
    'manager.measures': 'Mga Hakbang',
    'manager.caption': 'Paliwanag ng Larawan',
    'manager.criticalHazard': 'Kritikal na Salik ng Panganib',
    'manager.languageSwitchHint': 'Wika (para sa mga dayuhang manggagawa)',
    'badge.severity.critical': 'Kritikal',
    'badge.severity.high': 'Mataas',
    'badge.severity.medium': 'Katamtaman',
    'badge.severity.low': 'Mababa',
    'badge.signed': 'Nalagdaan na',
    'badge.waiting': 'Naghihintay',
    'badge.status.creating': 'Ginagawa',
    'badge.status.created': 'Nagawa na',
    'badge.status.reviewing': 'Sinusuri',
    'badge.status.inProgress': 'Isinasagawa',
    'badge.status.pendingApproval': 'Naghihintay ng Pag-apruba',
    'badge.status.completed': 'Nakumpleto',
    'badge.status.rejected': 'Tinanggihan',
    'badge.status.cancelled': 'Kinansela',
    'badge.sms.sent': 'Naipadala ang SMS',
    'badge.sms.failed': 'Nabigo ang SMS',
    'badge.approvalPending': 'Naghihintay',
    'badge.tag.urgent': 'Apurahan',
    'badge.tag.special': 'Espesyal',
    'badge.tag.extra': 'Karagdagan',
  },
  zh: {
    'common.loading': '加载中...',
    'common.error': '发生错误',
    'common.cancel': '取消',
    'common.confirm': '确认',
    'common.submit': '提交',
    'common.next': '下一步',
    'common.previous': '上一步',
    'common.close': '关闭',
    'common.languageSwitch': '选择语言',
    'sign.title': 'TBM培训签名',
    'sign.description': '请在下方输入您的姓名和签名，以确认参加TBM培训。',
    'sign.namePlaceholder': '请输入您的姓名',
    'sign.signaturePlaceholder': '签名区域',
    'sign.signButton': '完成签名',
    'sign.success': '签名已成功完成。',
    'sign.expired': '培训期限已过期。',
    'sign.alreadySigned': '此项目已签署。',
    'sign.invalidToken': '无效链接。',
    'sign.legalNotice': '此签名是具有法律效力的电子签名。',
    'educator.title': 'TBM培训课程',
    'educator.description': '请验证参与者并继续进行培训。',
    'educator.startEducation': '开始培训',
    'educator.completeEducation': '完成培训',
    'educator.attendeesTitle': '参与者名单',
    'educator.directSign': '直接签名',
    'educator.signatureRequired': '需要教师签名。',
    'educator.educatorSignature': '教师签名',
    'educator.legalNotice': '教师签名在法律上证明培训已完成。',
    'tbm.duration': '时长',
    'tbm.educator': '教师',
    'tbm.location': '地点',
    'tbm.contents': '内容',
    'tbm.workType': '工作类型',
    'tbm.specialNotes': '特殊说明',
    'tbm.safetyMeasures': '安全措施',
    'tbm.workDescription': '工作说明',
    'tbm.findings': '危险因素',
    'tbm.attendance': '出勤状况',
    'tbm.signedAt': '签名时间',
    'manager.attendees': '参与者',
    'manager.directSignReceive': '收集直接签名',
    'manager.signed': '已签名',
    'manager.notSigned': '未签名',
    'manager.educationDate': '培训日期',
    'manager.tbmContents': 'TBM内容',
    'manager.hazardFindings': '潜在危险因素',
    'manager.measures': '措施',
    'manager.caption': '照片说明',
    'manager.criticalHazard': '重点危险因素',
    'manager.languageSwitchHint': '语言（供外籍员工使用）',
    'badge.severity.critical': '严重',
    'badge.severity.high': '高',
    'badge.severity.medium': '中等',
    'badge.severity.low': '低',
    'badge.signed': '已签名',
    'badge.waiting': '等待中',
    'badge.status.creating': '创建中',
    'badge.status.created': '已创建',
    'badge.status.reviewing': '审核中',
    'badge.status.inProgress': '进行中',
    'badge.status.pendingApproval': '待审批',
    'badge.status.completed': '已完成',
    'badge.status.rejected': '已拒绝',
    'badge.status.cancelled': '已取消',
    'badge.sms.sent': 'SMS已发送',
    'badge.sms.failed': 'SMS发送失败',
    'badge.approvalPending': '待处理',
    'badge.tag.urgent': '紧急',
    'badge.tag.special': '特别',
    'badge.tag.extra': '额外',
  },
};

export function t(locale: Locale, key: TbmStaticKey): string {
  return TBM_STATIC_STRINGS[locale]?.[key] ?? TBM_STATIC_STRINGS.ko[key] ?? key;
}
